import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import Pagination from '../../components/Pagination.tsx';
import LoaderOverlay from '../../components/LoaderOverlay';
import layoutStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import reportStyles from '../../styles/ReportesIngresosDiarios.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { getReporteRectoria } from '../../api/reportesFinancieros';

// Número máximo de parcialidades a mostrar en la tabla (P1–P5)
const MAX_PARCIALIDADES = 5;

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const normalizeCicloKey = (value) =>
  normalizeText(value)
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/\s+/g, '');

const getFilterOptionKey = (option) =>
  normalizeText(option?.value ?? option?.id ?? option?.label ?? '');

const mergeFilterOptions = (currentOptions = [], nextOptions = []) => {
  const seen = new Set();
  const merged = [];

  [...currentOptions, ...nextOptions].forEach((option) => {
    if (!option) return;

    const key = getFilterOptionKey(option);
    if (!key || seen.has(key)) return;

    seen.add(key);
    merged.push(option);
  });

  return merged;
};

const getRowCicloValue = (row) =>
  row?.ciclo ?? row?.ciclo_escolar ?? row?.nombre_ciclo ?? row?.ciclo_nombre ?? '';

const applyMontoDescuentoToPresupuesto = (amount, descuentoMonto) => {
  const baseAmount = Number(amount ?? 0) || 0;
  const descuento = Number(descuentoMonto ?? 0) || 0;
  return Math.max(baseAmount - descuento, 0);
};

const createEmptyParcialidadTotals = () =>
  Array.from({ length: MAX_PARCIALIDADES }, () => ({
    deberiaPagar: 0,
    cuantoPaga: 0,
    bonificacion: 0,
    condonacion: 0,
    recargo: 0,
  }));

const ReporteRectoriaScreen = () => {
  const { isCollapsed } = useSidebar();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [selectedSeccion, setSelectedSeccion] = useState('');
  const [selectedPrograma, setSelectedPrograma] = useState('');
  const [page, setPage] = useState(1);
  const [availableFilters, setAvailableFilters] = useState({
    ciclos: [],
    secciones: [],
    programas: [],
  });
  const pageSize = 15;
  const reportRef = useRef(null);

  // Escuchar cambios de campus desde HeaderGlobal
  useEffect(() => {
    const handler = (e) => {
      const campus = (e.detail && e.detail.campus) || localStorage.getItem('selectedCampus') || '';
      setSelectedCampus(campus);
    };
    window.addEventListener('campusChanged', handler);
    setSelectedCampus(localStorage.getItem('selectedCampus') || '');
    return () => window.removeEventListener('campusChanged', handler);
  }, []);

  const fetchData = useCallback(async (targetPage = 1, exportAll = false, showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError(null);
      const response = await getReporteRectoria({
        campus: selectedCampus,
        cicloId: selectedCiclo,
        search: searchTerm,
        idSeccion: selectedSeccion,
        idPrograma: selectedPrograma,
        page: targetPage,
        page_size: pageSize,
        all: exportAll,
      });
      setReportData(response || null);
      setPage(response?.page || targetPage);
    } catch (err) {
      console.error('Error al cargar reporte de rectoría:', err);
      setError('Error al cargar el reporte de rectoría.');
      setReportData(null);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [pageSize, searchTerm, selectedCampus, selectedCiclo, selectedPrograma, selectedSeccion]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(1);
    }, 250);

    return () => clearTimeout(timeout);
  }, [fetchData, selectedCampus, selectedCiclo, selectedSeccion, selectedPrograma, searchTerm]);

  useEffect(() => {
    setAvailableFilters({
      ciclos: [],
      secciones: [],
      programas: [],
    });
  }, [selectedCampus]);

  useEffect(() => {
    const filtrosDisponibles = reportData?.filtros_disponibles;
    if (!filtrosDisponibles) return;

    setAvailableFilters((currentFilters) => ({
      ciclos: mergeFilterOptions(currentFilters.ciclos, filtrosDisponibles.ciclos),
      secciones: mergeFilterOptions(currentFilters.secciones, filtrosDisponibles.secciones),
      programas: mergeFilterOptions(currentFilters.programas, filtrosDisponibles.programas),
    }));
  }, [reportData]);

  const cicloOptions = useMemo(() => {
    return availableFilters.ciclos.length > 0
      ? availableFilters.ciclos
      : (reportData?.filtros_disponibles?.ciclos || []);
  }, [availableFilters.ciclos, reportData]);

  const seccionOptions = useMemo(() => (
    availableFilters.secciones.length > 0
      ? availableFilters.secciones
      : (reportData?.filtros_disponibles?.secciones || [])
  ), [availableFilters.secciones, reportData]);
  const programaOptions = useMemo(() => (
    availableFilters.programas.length > 0
      ? availableFilters.programas
      : (reportData?.filtros_disponibles?.programas || [])
  ), [availableFilters.programas, reportData]);

  const visibleRows = reportData?.results || [];
  const totalPages = Math.max(1, reportData?.total_pages || 1);
  const grandTotals = reportData?.grand_totals || {
    totalDeberiaPagar: 0,
    totalPaga: 0,
    totalBonificacion: 0,
    totalCondonacion: 0,
    totalRecargo: 0,
  };
  const parcialidadTotals = reportData?.parcialidad_totals || createEmptyParcialidadTotals();

  const handlePageChange = useCallback((newPage) => {
    fetchData(newPage, false, true);
  }, [fetchData]);

  const formatMoney = (value) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(value || 0);

  // ─── Descarga Excel ────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      setError(null);

      const exportData = await getReporteRectoria({
        campus: selectedCampus,
        cicloId: selectedCiclo,
        search: searchTerm,
        idSeccion: selectedSeccion,
        idPrograma: selectedPrograma,
        all: true,
      });

      const exportRows = exportData?.results || [];
      const exportGrandTotals = exportData?.grand_totals || grandTotals;
      const exportParcialidadTotals = exportData?.parcialidad_totals || parcialidadTotals;

      const wb = XLSX.utils.book_new();
      const sheetData = [];

      const pad = '\u00A0\u00A0\u00A0\u00A0\u00A0';
      sheetData.push([`${pad}Centro De Estudios Superiores CMP, A.C.`]);
      sheetData.push([`${pad}Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN`]);
      sheetData.push([`${pad}REPORTE DE PRESUPUESTO POR PERIODO`]);
      sheetData.push([]);

      const headerRow1 = ['No.', 'Nombre', 'Matricula', 'Beca', 'Ciclo'];
      for (let i = 1; i <= MAX_PARCIALIDADES; i++) {
        headerRow1.push(`Parcialidad ${i}`, '', '', '', '');
      }
      headerRow1.push('Total Que Debe Pagar', 'Total Que Paga', 'Total Bonificacion', 'Total Condonacion', 'Total Recargo');
      sheetData.push(headerRow1);

      const headerRow2 = ['', '', '', '', ''];
      for (let i = 1; i <= MAX_PARCIALIDADES; i++) {
        headerRow2.push('Presupuesto', 'Pagado', 'Bonificacion', 'Condonacion', 'Recargo');
      }
      headerRow2.push('', '', '', '', '');
      sheetData.push(headerRow2);

      exportRows.forEach((row, idx) => {
        const dataRow = [
          idx + 1,
          row.nombre || '',
          row.matricula || '',
          row.beca_display || row.beca || '',
          getRowCicloValue(row) || '',
        ];
        for (let i = 1; i <= MAX_PARCIALIDADES; i++) {
          const parc = row.parcialidades?.[String(i)] || {};
          dataRow.push(parc.deberia_pagar != null ? applyMontoDescuentoToPresupuesto(parc.deberia_pagar, parc.monto_descuento) : '');
          dataRow.push(parc.cuanto_paga != null ? Number(parc.cuanto_paga) : '');
          dataRow.push(parc.descuento_bonificacion != null ? Number(parc.descuento_bonificacion) : '');
          dataRow.push(parc.descuento_condonacion != null ? Number(parc.descuento_condonacion) : '');
          dataRow.push(parc.recargo != null ? Number(parc.recargo) : '');
        }
        const tDeberia = Object.values(row.parcialidades || {}).length > 0
          ? Object.values(row.parcialidades || {}).reduce(
              (sum, parcialidad) => sum + applyMontoDescuentoToPresupuesto(parcialidad?.deberia_pagar, parcialidad?.monto_descuento),
              0
            )
          : row.total_deberia_pagar != null
            ? Number(row.total_deberia_pagar)
            : '';
        const tPaga = row.total_paga != null ? Number(row.total_paga) : '';
        const tBonificacion = row.total_descuento_bonificacion != null ? Number(row.total_descuento_bonificacion) : '';
        const tCondonacion = row.total_descuento_condonacion != null ? Number(row.total_descuento_condonacion) : '';
        const tRecargo = row.total_recargo != null ? Number(row.total_recargo) : '';
        dataRow.push(tDeberia, tPaga, tBonificacion, tCondonacion, tRecargo);
        sheetData.push(dataRow);
      });

      const totalRow = ['', 'TOTAL GENERAL', '', '', ''];
      for (let i = 0; i < MAX_PARCIALIDADES; i++) {
        const t = exportParcialidadTotals[i];
        totalRow.push(t.deberiaPagar, t.cuantoPaga, t.bonificacion, t.condonacion, t.recargo);
      }
      totalRow.push(
        exportGrandTotals.totalDeberiaPagar,
        exportGrandTotals.totalPaga,
        exportGrandTotals.totalBonificacion,
        exportGrandTotals.totalCondonacion,
        exportGrandTotals.totalRecargo,
      );
      sheetData.push(totalRow);

      const sheet = XLSX.utils.aoa_to_sheet(sheetData);

      const totalCols = 5 + MAX_PARCIALIDADES * 5 + 5 - 1;
      const HDR = 4;

      const merges = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: totalCols } },
        { s: { r: HDR, c: 0 }, e: { r: HDR + 1, c: 0 } },
        { s: { r: HDR, c: 1 }, e: { r: HDR + 1, c: 1 } },
        { s: { r: HDR, c: 2 }, e: { r: HDR + 1, c: 2 } },
        { s: { r: HDR, c: 3 }, e: { r: HDR + 1, c: 3 } },
        { s: { r: HDR, c: 4 }, e: { r: HDR + 1, c: 4 } },
      ];

      for (let i = 0; i < MAX_PARCIALIDADES; i++) {
        const startCol = 5 + i * 5;
        merges.push({ s: { r: HDR, c: startCol }, e: { r: HDR, c: startCol + 4 } });
      }

      const totalStartCol = 5 + MAX_PARCIALIDADES * 5;
      for (let i = 0; i < 5; i++) {
        merges.push({ s: { r: HDR, c: totalStartCol + i }, e: { r: HDR + 1, c: totalStartCol + i } });
      }

      sheet['!merges'] = merges;

      const setStyle = (r, c, style) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!sheet[addr]) sheet[addr] = { v: '', t: 's' };
        sheet[addr].s = style;
      };

      const center = { horizontal: 'center', vertical: 'center', wrapText: true };

      setStyle(0, 0, { font: { bold: true, sz: 14 }, alignment: center });
      setStyle(1, 0, { font: { bold: true, sz: 11 }, alignment: center });
      setStyle(2, 0, { font: { bold: true, sz: 11 }, alignment: center });

      const fixedHdrStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '1E3A8A' } },
        alignment: center,
      };
      for (let c = 0; c < 5; c++) {
        setStyle(HDR, c, fixedHdrStyle);
        setStyle(HDR + 1, c, fixedHdrStyle);
      }

      for (let i = 0; i < MAX_PARCIALIDADES; i++) {
        const startCol = 5 + i * 5;
        const bgGroup = i % 2 === 0 ? 'FEF08A' : 'E5E7EB';
        const bgSub = i % 2 === 0 ? 'FEF9C3' : 'F3F4F6';
        const parcGroupStyle = {
          font: { bold: true },
          fill: { fgColor: { rgb: bgGroup } },
          alignment: center,
        };
        const parcSubStyle = {
          font: { bold: true },
          fill: { fgColor: { rgb: bgSub } },
          alignment: center,
        };
        setStyle(HDR, startCol, parcGroupStyle);
        for (let j = 0; j < 5; j++) {
          setStyle(HDR + 1, startCol + j, parcSubStyle);
        }
      }

      const totalHdrStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '0097A7' } },
        alignment: center,
      };
      for (let i = 0; i < 5; i++) {
        setStyle(HDR, totalStartCol + i, totalHdrStyle);
        setStyle(HDR + 1, totalStartCol + i, totalHdrStyle);
      }

      const dataStartRow = HDR + 2;
      const dataTotalStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'E0F7FA' } },
        alignment: { horizontal: 'right' },
      };
      exportRows.forEach((_, rowIdx) => {
        for (let i = 0; i < 5; i++) {
          setStyle(dataStartRow + rowIdx, totalStartCol + i, dataTotalStyle);
        }
      });

      const totalGeneralRowIdx = dataStartRow + exportRows.length;
      const totalRowCols = 5 + MAX_PARCIALIDADES * 5 + 5;
      const totalGeneralBaseStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'B2EBF2' } },
        alignment: center,
      };
      const totalGeneralAccentStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '0097A7' } },
        alignment: { horizontal: 'right' },
      };
      for (let c = 0; c < totalRowCols; c++) {
        setStyle(totalGeneralRowIdx, c, c >= totalStartCol ? totalGeneralAccentStyle : totalGeneralBaseStyle);
      }

      const moneyFormat = '"$"#,##0.00';
      exportRows.forEach((_, rowIdx) => {
        const r = dataStartRow + rowIdx;
        for (let c = 5; c < 5 + MAX_PARCIALIDADES * 5 + 5; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (sheet[addr] && sheet[addr].t === 'n') sheet[addr].z = moneyFormat;
        }
      });
      for (let c = 5; c < 5 + MAX_PARCIALIDADES * 5 + 5; c++) {
        const addr = XLSX.utils.encode_cell({ r: totalGeneralRowIdx, c });
        if (sheet[addr] && sheet[addr].t === 'n') sheet[addr].z = moneyFormat;
      }

      const colWidths = [
        { wch: 5 },
        { wch: 35 },
        { wch: 18 },
        { wch: 8 },
        { wch: 20 },
      ];
      for (let i = 0; i < MAX_PARCIALIDADES * 5; i++) colWidths.push({ wch: 14 });
      for (let i = 0; i < 5; i++) colWidths.push({ wch: 18 });
      sheet['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, sheet, 'Reporte Activos por Mes');
      XLSX.writeFile(wb, 'REPORTE DE PRESUPUESTO POR PERIODO.xlsx', { cellStyles: true });
    } catch (err) {
      console.error('Error al descargar Excel del reporte de rectoría:', err);
      setError('Error al generar el archivo Excel.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={layoutStyles.mainContainer} style={{ height: '100vh', display: 'flex' }}>
      <div
        className={`${layoutStyles.layout} ${isCollapsed ? layoutStyles.sidebarCollapsedLayout : ''}`}
        style={{ flex: 1, display: 'flex', height: '100vh' }}
      >
        <div className={layoutStyles.sidebarColumn} style={{ height: '100vh', minHeight: 0 }}>
          <Sidebar />
        </div>

        <div
          className={layoutStyles.contentColumn}
          style={{ position: 'relative', height: '100vh', overflowY: 'auto', minHeight: 0 }}
        >
          <LoaderOverlay visible={loading} label="Cargando reporte de activos por mes..." />
          <HeaderGlobal
            title="REPORTE DE PRESUPUESTO POR PERIODO"
            campus={selectedCampus}
            onCampusChange={(e) => setSelectedCampus(e.target?.value || '')}
          />

          <main className={layoutStyles.mainContent}>
            <div className={layoutStyles.contentArea}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* ── Filtros ── */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>

                    <div className={estudiantesStyles.searchRow}>
                      {/* Filtro Ciclo Escolar */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Ciclo Escolar</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          value={selectedCiclo}
                          onChange={e => { setSelectedCiclo(e.target.value); setPage(1); }}
                        >
                          <option value="">Todos</option>
                          {cicloOptions.map((ciclo) => (
                            <option key={ciclo.value} value={ciclo.value}>{ciclo.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Búsqueda */}
                      <div className={`${estudiantesStyles.formGroup} ${estudiantesStyles['search-buscador']}`}> 
                        <label className={estudiantesStyles.searchLabel}>Matrícula o nombre</label>
                        <div className={estudiantesStyles.searchInputGroup}>
                          <svg
                            className={estudiantesStyles.searchIcon}
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M21 21L15 15M10 17C13.866 17 17 13.866 17 10C17 6.134 13.866 3 10 3C6.134 3 3 6.134 3 10C3 13.866 6.134 17 10 17Z"
                              stroke="#898d8d"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className={estudiantesStyles.searchInput}
                            placeholder="Buscar por matrícula o nombre"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                          />
                        </div>
                      </div>

                      {/* Filtro Sección */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Sección</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          value={selectedSeccion}
                          onChange={e => { setSelectedSeccion(e.target.value); setPage(1); }}
                        >
                          <option value="">Todos</option>
                          {seccionOptions.map((seccion) => (
                            <option key={seccion.value} value={seccion.value}>{seccion.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Filtro Programa */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Programa</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          value={selectedPrograma}
                          onChange={e => { setSelectedPrograma(e.target.value); setPage(1); }}
                        >
                          <option value="">Todos</option>
                          {programaOptions.map((programa) => (
                            <option key={programa.value} value={programa.value}>{programa.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Limpiar */}
                      <div className={reportStyles.filterActions}>
                        <button
                          type="button"
                          className={reportStyles.clearFiltersButton}
                          onClick={() => {
                            setSearchTerm('');
                            setSelectedCiclo('');
                            setSelectedSeccion('');
                            setSelectedPrograma('');
                            setPage(1);
                          }}
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Contenedor del reporte ── */}
                <div
                  className={`${layoutStyles.contentWrapper} ${reportStyles.reportWrapper}`}
                  ref={reportRef}
                >
                  <div className={reportStyles.reportContainer}>
                    {/* Barra de acciones */}
                    <div className={reportStyles.actionsBar}>
                      <button
                        type="button"
                        className={`${reportStyles.downloadButton} bg-blue-900 text-white border-transparent`}
                        onClick={handleDownloadExcel}
                        style={{ backgroundColor: '#1e3a8a', color: '#ffffff', borderColor: 'transparent' }}
                      >
                        <span className={reportStyles.downloadIcon}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 3v12m0 0 4-4m-4 4-4-4"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 15v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span>Descargar Excel</span>
                      </button>
                    </div>

                    {/* Encabezado institucional */}
                    <header className={reportStyles.reportHeader}>
                      <div className={reportStyles.headerLeft}>
                        <div className={reportStyles.logoPlaceholder} />
                      </div>
                      <div className={reportStyles.headerCenter}>
                        <h2 className={reportStyles.institutionName}>
                          Centro De Estudios Superiores CMP, A.C.
                        </h2>
                        <div className={reportStyles.institutionAddress}>
                          Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN
                        </div>
                        <div className={reportStyles.reportTitleRow}>
                          <span>Reporte de Presupuesto por Periodo</span>
                        </div>
                      </div>
                    </header>

                    {/* ── Tabla ── */}
                    <section className={reportStyles.tableSection}>
                      {error && (
                        <div style={{ color: '#dc2626', textAlign: 'center', padding: '12px', fontSize: '0.9rem' }}>
                          {error}
                        </div>
                      )}
                      <div className={reportStyles.tableScrollWrapper}>
                        <table className={reportStyles.dataTable}>
                          <thead>
                            <tr>
                              <th rowSpan={2}>No.</th>
                              <th rowSpan={2} className={reportStyles.stickyCol}>Nombre</th>
                              <th rowSpan={2}>Matricula</th>
                              <th rowSpan={2}>Beca</th>
                              <th rowSpan={2} style={{ minWidth: '140px' }}>Ciclo</th>
                              {Array.from({ length: MAX_PARCIALIDADES }, (_, i) => (
                                <th key={i} colSpan={5} style={{ textAlign: 'center', background: i % 2 === 0 ? '#fef08a' : undefined }}>
                                  Parcialidad {i + 1}
                                </th>
                              ))}
                              <th rowSpan={2} className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff', whiteSpace: 'nowrap' }}>Total Que Debe Pagar</th>
                              <th rowSpan={2} className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff', whiteSpace: 'nowrap' }}>Total Que Paga</th>
                              <th rowSpan={2} className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff', whiteSpace: 'nowrap' }}>Total Bonificacion</th>
                              <th rowSpan={2} className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff', whiteSpace: 'nowrap' }}>Total Condonacion</th>
                              <th rowSpan={2} className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff', whiteSpace: 'nowrap' }}>Total Recargo</th>
                            </tr>
                            <tr>
                              {Array.from({ length: MAX_PARCIALIDADES }, (_, i) => (
                                <React.Fragment key={i}>
                                  <th className={reportStyles.numeric} style={{ background: i % 2 === 0 ? '#fef9c3' : undefined }}>
                                    Presupuesto
                                  </th>
                                  <th className={reportStyles.numeric} style={{ background: i % 2 === 0 ? '#fef9c3' : undefined }}>
                                    Pagado
                                  </th>
                                  <th className={reportStyles.numeric} style={{ background: i % 2 === 0 ? '#fef9c3' : undefined }}>
                                    Bonificacion
                                  </th>
                                  <th className={reportStyles.numeric} style={{ background: i % 2 === 0 ? '#fef9c3' : undefined }}>
                                    Condonacion
                                  </th>
                                  <th className={reportStyles.numeric} style={{ background: i % 2 === 0 ? '#fef9c3' : undefined }}>
                                    Recargo
                                  </th>
                                </React.Fragment>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.length === 0 && !loading && (
                                  <tr>
                                <td
                                  colSpan={5 + MAX_PARCIALIDADES * 5 + 5}
                                  style={{ textAlign: 'center', color: '#777', fontSize: '0.9rem', padding: '16px' }}
                                >
                                  {error ? '' : 'No hay datos para mostrar.'}
                                </td>
                              </tr>
                            )}
                            {visibleRows.map((row, idx) => {
                              const rowNumber = ((page - 1) * pageSize) + idx + 1;
                              let totalDeberia = 0;
                              let totalPaga = 0;
                              let totalBonificacion = 0;
                              let totalCondonacion = 0;
                              let totalRecargo = 0;
                              for (let i = 1; i <= MAX_PARCIALIDADES; i++) {
                                totalDeberia += applyMontoDescuentoToPresupuesto(
                                  row.parcialidades?.[String(i)]?.deberia_pagar,
                                  row.parcialidades?.[String(i)]?.monto_descuento,
                                );
                                totalPaga += row.parcialidades?.[String(i)]?.cuanto_paga || 0;
                                totalBonificacion += row.parcialidades?.[String(i)]?.descuento_bonificacion || 0;
                                totalCondonacion += row.parcialidades?.[String(i)]?.descuento_condonacion || 0;
                                totalRecargo += row.parcialidades?.[String(i)]?.recargo || 0;
                              }
                              return (
                                <tr key={`${row.id_estudiante ?? 'row'}-${rowNumber}`}>
                                  <td className={reportStyles.center}>{rowNumber}</td>
                                  <td className={reportStyles.stickyCol}>{row.nombre}</td>
                                  <td className={reportStyles.center}>{row.matricula || '—'}</td>
                                  <td className={reportStyles.center}>{row.beca_display || row.beca || '—'}</td>
                                  <td className={reportStyles.center} style={{ minWidth: '140px' }}>{getRowCicloValue(row) || '—'}</td>
                                  {Array.from({ length: MAX_PARCIALIDADES }, (_, i) => {
                                    const parc = row.parcialidades?.[String(i + 1)] || {};
                                    const presupuestoConDescuento = applyMontoDescuentoToPresupuesto(parc.deberia_pagar, parc.monto_descuento);
                                    return (
                                      <React.Fragment key={i}>
                                        <td className={reportStyles.numeric}>
                                          {parc.deberia_pagar != null ? formatMoney(presupuestoConDescuento) : '—'}
                                        </td>
                                        <td className={reportStyles.numeric}>
                                          {parc.cuanto_paga != null ? formatMoney(parc.cuanto_paga) : '—'}
                                        </td>
                                        <td className={reportStyles.numeric}>
                                          {parc.descuento_bonificacion != null ? formatMoney(parc.descuento_bonificacion) : '—'}
                                        </td>
                                        <td className={reportStyles.numeric}>
                                          {parc.descuento_condonacion != null ? formatMoney(parc.descuento_condonacion) : '—'}
                                        </td>
                                        <td className={reportStyles.numeric}>
                                          {parc.recargo != null ? formatMoney(parc.recargo) : '—'}
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                  <td className={reportStyles.numeric} style={{ fontWeight: 600, background: '#e0f7fa' }}>
                                    {formatMoney(totalDeberia)}
                                  </td>
                                  <td className={reportStyles.numeric} style={{ fontWeight: 600, background: '#e0f7fa' }}>
                                    {formatMoney(row.total_paga ?? totalPaga)}
                                  </td>
                                  <td className={reportStyles.numeric} style={{ fontWeight: 600, background: '#e0f7fa' }}>
                                    {formatMoney(row.total_descuento_bonificacion ?? totalBonificacion)}
                                  </td>
                                  <td className={reportStyles.numeric} style={{ fontWeight: 600, background: '#e0f7fa' }}>
                                    {formatMoney(row.total_descuento_condonacion ?? totalCondonacion)}
                                  </td>
                                  <td className={reportStyles.numeric} style={{ fontWeight: 600, background: '#e0f7fa' }}>
                                    {formatMoney(row.total_recargo ?? totalRecargo)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: '#b2ebf2', fontWeight: 700 }}>
                              <td className={reportStyles.center}></td>
                              <td className={reportStyles.stickyCol} style={{ fontWeight: 700 }}>TOTAL GENERAL</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              {parcialidadTotals.map((totales, i) => (
                                <React.Fragment key={`tfoot-parcialidad-${i}`}>
                                  <td className={reportStyles.numeric}>{formatMoney(totales.deberiaPagar)}</td>
                                  <td className={reportStyles.numeric}>{formatMoney(totales.cuantoPaga)}</td>
                                  <td className={reportStyles.numeric}>{formatMoney(totales.bonificacion)}</td>
                                  <td className={reportStyles.numeric}>{formatMoney(totales.condonacion)}</td>
                                  <td className={reportStyles.numeric}>{formatMoney(totales.recargo)}</td>
                                </React.Fragment>
                              ))}
                              <td className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff' }}>
                                {formatMoney(grandTotals.totalDeberiaPagar)}
                              </td>
                              <td className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff' }}>
                                {formatMoney(grandTotals.totalPaga)}
                              </td>
                              <td className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff' }}>
                                {formatMoney(grandTotals.totalBonificacion)}
                              </td>
                              <td className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff' }}>
                                {formatMoney(grandTotals.totalCondonacion)}
                              </td>
                              <td className={reportStyles.numeric} style={{ background: '#0097a7', color: '#fff' }}>
                                {formatMoney(grandTotals.totalRecargo)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </section>

                    {totalPages > 1 && (
                      <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReporteRectoriaScreen;
